import cn from 'classnames';
import { Fragment, ReactNode } from 'react';

import { type DayGroup } from '@/shared/config';

import styles from './Timeline.module.scss';
import { TimelineCard } from './TimelineCard';
import { TimelineDayLabel } from './TimelineDayLabel';
import { TimelineRow } from './TimelineRow';

type TimelineProps<T> = {
  groups: DayGroup<T>[];
  className?: string;
  dataComponent?: string;
  getItemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  renderDayMeta?: (group: DayGroup<T>) => ReactNode;
};

const TimelineComponent = <T,>(props: TimelineProps<T>) => {
  const {
    groups,
    className,
    dataComponent,
    getItemKey,
    renderItem,
    renderDayMeta,
  } = props;

  return (
    <div
      className={cn(styles.timeline, className)}
      data-component={dataComponent}
    >
      {groups.map((group) => (
        <div
          key={group.dayKey}
          className={styles.group}
          data-component="timeline-day-group"
        >
          <TimelineDayLabel
            date={group.dayDate}
            meta={renderDayMeta?.(group)}
            className={styles.dayLabel}
          />
          <div className={styles.marker}>
            <span className={styles.dot} />
          </div>
          <div className={styles.dayCard}>
            {group.items.map((item) => (
              <Fragment key={getItemKey(item)}>{renderItem(item)}</Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

type CompoundedTimeline = typeof TimelineComponent & {
  DayLabel: typeof TimelineDayLabel;
  Row: typeof TimelineRow;
  Card: typeof TimelineCard;
};

const Timeline = TimelineComponent as CompoundedTimeline;

Timeline.DayLabel = TimelineDayLabel;
Timeline.Row = TimelineRow;
Timeline.Card = TimelineCard;

export { Timeline };
export type { TimelinePreview } from './Timeline.types';
